/**
 * Labstep
 *
 * @module BatchCreateResourceItems/services/grid-batch-create-resource-items-service
 * @desc AGGrid Service
 */

import type {
  CellClassParams,
  CellStyle,
  ColDef,
  ValueSetterParams,
} from 'ag-grid-community';
import { parse } from 'date-fns';
import type {
  errorType,
  ResourceItemData,
} from 'labstep-web/components/ResourceItem/Action/BulkCreate/Modal/type';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridColumnHeaderEntityImport } from 'labstep-web/core/DataGrid/ColumnHeader/EntityImport';
import { colDefBatchCreateResourceItemsAddColumn } from 'labstep-web/grid/BatchCreateResourceItems/coldefs/add-column';
import { getResourceItemAmountColDef } from 'labstep-web/grid/BatchCreateResourceItems/coldefs/amount';
import { getLocationPathColDef } from 'labstep-web/grid/BatchCreateResourceItems/coldefs/locationPath';
import { getMetadataColDef } from 'labstep-web/grid/BatchCreateResourceItems/coldefs/metadata';
import { getResourceItemColDef } from 'labstep-web/grid/BatchCreateResourceItems/coldefs/resourceItem';
import { GridEntityImportValidationService } from 'labstep-web/grid/EntityImport/services/grid-entity-import-validation.service';
import { GridService } from 'labstep-web/grid/services/grid.service';
import type { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import type { OrderRequest } from 'labstep-web/models/order-request.model';
import type { Resource } from 'labstep-web/models/resource.model';
import type { ResourceItem } from 'labstep-web/models/resource-item.model';
import { formatDateOnly } from 'labstep-web/services/date.service';
import { showToast } from 'labstep-web/state/actions/ui';
import store from 'labstep-web/state/store';

export const ENTITY_DATE_FORMATS = [
  'dd/MM/yyyy',
  'dd-MM-yyyy',
  'dd.MM.yyyy',
  'ddMMyyyy',
  'ddMMyy',
  'yyyy/MM/dd',
  'yyyy-MM-dd',
  'yyyy.MM.dd',
  'yyyyMMdd',
  'yyMMdd',
  'MM/dd/yyyy',
  'MM-dd-yyyy',
  'MM.dd.yyyy',
];

export const ENTITY_DATETIME_FORMATS = [
  'dd/MM/yyyy HH:mm',
  'dd/MM/yyyy HH:mm:ss',
  'dd-MM-yyyy',
  'dd-MM-yyyy HH:mm:ss',
  'yyyy/MM/dd HH:mm',
  'yyyy/MM/dd HH:mm:ss',
  'yyyy-MM-dd HH:mm',
  'yyyy-MM-dd HH:mm:ss',
];
export class GridBatchCreateResourceItemsService extends GridService {
  public static startEditingCell = (params: any): void => {
    params.api.startEditingCell({
      rowIndex: params.node!.rowIndex!,
      colKey: params.column.getColId(),
    });
  };

  public static getDefaultColDefs = (
    resource: Resource,
    data: ResourceItemData[],
    setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
    dateFormats: Record<string, string>,
  ) => {
    const colDefs: ColDef[] = [
      getResourceItemColDef(data, setData, resource),
      getLocationPathColDef(data, setData),
      getResourceItemAmountColDef(data, setData),
    ];

    const itemTemplate =
      resource.resource_item_template ||
      resource.template?.resource_item_template;

    if (itemTemplate) {
      colDefs.push(
        ...this.getItemTemplateMetadataCodefs(
          itemTemplate,
          resource,
          data,
          setData,
          dateFormats,
        ),
      );
    }

    // Add the "Add Column" button column at the end
    colDefs.push(colDefBatchCreateResourceItemsAddColumn(resource));

    return colDefs;
  };

  public static get defaultColDef(): ColDef & { valueClearer: null } {
    return {
      editable: true,
      lockVisible: true,
      headerComponent: DataGridColumnHeaderEntityImport,
      // needed for when resource name changes, to update the item name
      // and to check if row is not empty anymore / is valid
      onCellValueChanged: (params) => {
        const rowNode = params.node;
        if (!rowNode) {
          return;
        }
        params.api.refreshCells({
          rowNodes: [rowNode],
          force: true,
        });
      },
      cellStyle: (
        params: CellClassParams,
      ): CellStyle | null | undefined => {
        // Don't show validation errors for rows without names
        if (!params.data?.name || params.data.name.trim() === '') {
          return null;
        }
        if (
          GridEntityImportValidationService.isRowEmpty(params.data)
        ) {
          return null;
        }
        if (!GridEntityImportValidationService.isCellValid(params)) {
          return { backgroundColor: '#F5B1B1' };
        }
        return null;
      },
      suppressKeyboardEvent: suppressKeyboardEventClearCells,
      valueClearer: null,
    };
  }

  public static getValueSetterForColId = (
    colId: string,
    data: ResourceItemData[],
    setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
  ) => {
    return (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      const row = params.node.data;
      row[colId] = params.newValue;
      data[params.node.rowIndex!] = row;
      setData(data);
      return true;
    };
  };

  public static getMetadataValue = (metadata: Metadata) => {
    switch (metadata.type) {
      case MetadataType.numeric:
        return metadata.number;
      case MetadataType.date:
        return metadata.date;
      case MetadataType.datetime:
        return metadata.date;
      case MetadataType.file:
        return metadata.files;
      case MetadataType.options:
        return metadata.options;
      case MetadataType.sequence:
        return metadata.sequence;
      case MetadataType.entity_relation:
        return metadata.entity_relation_preview;
      default:
        return metadata.value;
    }
  };

  public static triggerValidationError = (message: string): void => {
    store.dispatch(
      showToast({
        type: 'error',
        message: message,
        options: {
          header: 'Validation error',
          timeout: 6000,
        },
      }),
    );
  };

  /**
   * Helper function to check which required fields are empty and create error objects
   */
  public static initializeRequiredFieldErrors = (
    row: ResourceItemData,
    rowIndex: number,
  ): errorType[] => {
    const errors: errorType[] = [];
    const requiredFields = row.requiredFields || [];
    requiredFields.forEach((fieldId: string) => {
      const value = row[fieldId];
      let isEmpty = false;

      // Check if field is empty
      if (value === null || value === undefined || value === '') {
        isEmpty = true;
      } else if (typeof value === 'object') {
        // For metadata objects, check if they have actual values
        if (value.date !== undefined) {
          isEmpty = !value.date;
        } else if (value.number !== undefined) {
          isEmpty =
            value.number === null || value.number === undefined;
        } else if (value.values !== undefined) {
          // Check if all values in the object are false
          isEmpty =
            !value.values ||
            Object.values(value.values).every((v) => v === false);
        } else if (value.value !== undefined) {
          isEmpty = !value.value;
        }
      }

      if (isEmpty) {
        errors.push({
          colId: fieldId,
          rowIndex: rowIndex,
          message: 'This field is required.',
        });
      }
    });

    return errors;
  };

  public static createEmptyRow = (
    resource: Resource,
  ): ResourceItemData => {
    const requiredFields: string[] = [];
    const templateData: Record<string, any> = {};

    const itemTemplate =
      resource.resource_item_template ||
      resource.template?.resource_item_template;

    if (itemTemplate) {
      const metadatas = itemTemplate.metadata_thread.metadatas;
      metadatas.forEach((metadata) => {
        templateData[`metadata:${metadata.type}:${metadata.guid}`] =
          this.getMetadataValue(metadata);
        if (metadata.is_required) {
          requiredFields.push(
            `metadata:${metadata.type}:${metadata.guid}`,
          );
        }
      });
    }

    return {
      resource_id: resource.id,
      name: '',
      resource_location_path:
        resource.itemTemplateLocation?.path || '',
      metadatas: [],
      ...templateData,
      errors: [],
      requiredFields: requiredFields,
      _rowIndex: -1,
    };
  };
  public static getInitialData = (
    resource: Resource,
    N = 5,
    orderRequest?: OrderRequest,
    includeBlankRow = true,
  ) => {
    const { itemTemplateLocation } = resource;
    const rows = Math.min(N, 50);

    const locationPath =
      itemTemplateLocation && itemTemplateLocation.path;

    const name = resource.custom_identifier
      ? `${resource.custom_identifier} ${resource.name || ''}`
      : resource.name || '';

    const data: ResourceItemData[] = [];
    const templateData: Record<string, any> = {};
    const requiredFields: string[] = [];

    const itemTemplate =
      resource.resource_item_template ||
      resource.template?.resource_item_template;

    if (itemTemplate) {
      const metadatas = itemTemplate.metadata_thread.metadatas;
      metadatas.forEach((metadata) => {
        templateData[`metadata:${metadata.type}:${metadata.guid}`] =
          this.getMetadataValue(metadata);
        if (metadata.is_required) {
          requiredFields.push(
            `metadata:${metadata.type}:${metadata.guid}`,
          );
        }
      });
    }

    for (let i = 1; i <= rows; i += 1) {
      const rowData: ResourceItemData = {
        resource_id: resource.id,
        name: `${resource.displayName ? resource.displayName : name} #${
          resource.resource_item_count + i
        }`,
        resource_location_path: locationPath,
        metadatas: [],
        ...(orderRequest && { order_request_id: orderRequest.id }),
        ...templateData,
        errors: [],
        requiredFields: requiredFields,
        _rowIndex: i - 1,
      };

      // Initialize errors for empty required fields
      rowData.errors = this.initializeRequiredFieldErrors(
        rowData,
        i - 1,
      );

      data.push(rowData);
    }

    if (includeBlankRow) {
      data.push({
        resource_id: resource.id,
        requiredFields: requiredFields,
      });
    }

    return data;
  };

  public onFilterChanged(): void {
    const { api } = this.agGrid;

    api.onFilterChanged();
  }

  public static getItemTemplateMetadataCodefs = (
    resourceItem: ResourceItem,
    resource: Resource,
    data: ResourceItemData[],
    setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
    dateFormats: Record<string, string>,
  ) => {
    const colDefs: ColDef[] = [];
    const metadatas = resourceItem.metadata_thread.metadatas;

    metadatas.forEach((metadata) => {
      colDefs.push(
        getMetadataColDef(
          metadata,
          resource,
          data,
          setData,
          dateFormats,
        ),
      );
    });
    return colDefs;
  };

  public static getMetadataBody = (
    metadata: Partial<Metadata>,
    metadataTemplate: Metadata,
  ): Partial<Metadata> => {
    const body: Record<string, unknown> = {
      type: metadataTemplate.type,
      label: metadataTemplate.label,
      template_id: metadataTemplate.guid,
      ...metadata,
    };

    return body;
  };

  public addRows = (
    data: ResourceItemData[],
    setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
    resource: Resource,
    dateFormats: Record<string, string>,
    numberOfRows = 1,
  ): void => {
    // Create blank rows without names
    const newRows: ResourceItemData[] = [];
    for (let i = 0; i < numberOfRows; i++) {
      newRows.push(
        GridBatchCreateResourceItemsService.createEmptyRow(resource),
      );
    }

    const newData = [...data, ...newRows];

    //setting coldefs again to force ag-grid to recognize new rows
    this.agGrid.api.setColumnDefs(
      GridBatchCreateResourceItemsService.getDefaultColDefs(
        resource,
        newData,
        setData,
        dateFormats,
      ),
    );

    setData(newData);
  };

  public static convertMetadataDateToISO = (
    metadata: Partial<Metadata>,
    format?: string,
  ) => {
    if (!metadata?.date) {
      return metadata;
    }
    const defaultFormat =
      metadata.type === MetadataType.date
        ? ENTITY_DATE_FORMATS[0]
        : ENTITY_DATETIME_FORMATS[0];

    const date = parse(
      metadata.date,
      format || defaultFormat,
      new Date(),
    );

    const isoDate = formatDateOnly(date);

    return {
      ...metadata,
      date: isoDate,
    };
  };

  public static checkValidData = (data: ResourceItemData[]) => {
    return !data.some((row) => {
      // Skip validation for empty rows (rows without names)
      if (!row.name || row.name.trim() === '') {
        return false;
      }
      // Check for required field errors only in rows that have names
      return row.errors?.some(
        (err) => err.message === 'This field is required.',
      );
    });
  };

  public static convertToPostData = (
    data: ResourceItemData[],
    dateFormats: Record<string, string>,
  ) => {
    const postData: any[] = [];

    data.forEach((row) => {
      // Skip rows that don't have a name (empty rows)
      if (!row.name || row.name.trim() === '') {
        return;
      }

      const newRow = { ...row };
      const metadatas: any = [];
      Object.keys(row).forEach((key, index) => {
        if (key.startsWith('metadata:')) {
          const splitColId = key.split(':');
          const templateGuid = splitColId[2];

          if (row[key] === null || row[key] === undefined) {
            delete newRow[key];
            return;
          }
          if (
            splitColId[1] === MetadataType.date ||
            splitColId[1] === MetadataType.datetime
          ) {
            metadatas.push({
              ...GridBatchCreateResourceItemsService.convertMetadataDateToISO(
                row[key],
                dateFormats[index],
              ),
              template_id: templateGuid,
            });
          } else {
            metadatas.push({
              ...row[key],
              template_id: templateGuid,
            });
          }
          delete newRow[key];
        }
      });
      if (!newRow.resource_location_path) {
        delete newRow.resource_location_path;
      }
      delete newRow.errors;
      delete newRow.requiredFields;
      delete newRow._rowIndex;
      newRow.metadatas = metadatas;
      postData.push(newRow);
    });

    return postData;
  };

  public static insertErrorInData = (
    data: ResourceItemData[],
    setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
    params: ValueSetterParams,
    errorMessage: string,
  ) => {
    if (!params.node) {
      return;
    }

    const row = data[params.node.rowIndex!];
    if (!row) {
      return;
    }
    const existingErrors =
      row.errors?.filter(
        (error) =>
          error.colId !== params.column.getColId() ||
          error.rowIndex !== params.node!.rowIndex,
      ) || [];

    const error = {
      colId: params.column.getColId(),
      rowIndex: params.node.rowIndex!,
      message: errorMessage,
    };

    existingErrors.push(error);

    row.errors = existingErrors;

    data[params.node.rowIndex!] = row;
    setData(data);
  };

  public static findErrorInData = (
    data: ResourceItemData[],
    params: any,
  ): errorType | null => {
    if (!params.node) {
      return null;
    }

    const row = data[params.node.rowIndex!];
    if (!row) {
      return null;
    }

    const existingErrors = row.errors || [];

    const error = existingErrors.find(
      (error) =>
        error.colId === params.column.getColId() &&
        error.rowIndex === params.node!.rowIndex,
    );

    return error || null;
  };

  public static removeErrorInData = (
    data: ResourceItemData[],
    setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
    params: any,
  ) => {
    if (!params.node) {
      return;
    }
    const row = data[params.node.rowIndex!];

    const existingErrors =
      row.errors?.filter(
        (error) =>
          error.colId !== params.column.getColId() ||
          error.rowIndex !== params.node!.rowIndex,
      ) || [];

    row.errors = existingErrors;

    data[params.node.rowIndex!] = row;
    setData(data);
  };

  public static getRequiredFields = (
    resource: Resource,
  ): string[] => {
    const requiredFields: string[] = ['name'];
    const itemTemplate =
      resource.resource_item_template ||
      resource.template?.resource_item_template;

    if (itemTemplate) {
      itemTemplate.metadata_thread.metadatas.forEach(
        (metadata: Metadata) => {
          if (metadata.is_required) {
            requiredFields.push(
              `metadata:${metadata.type}:${metadata.guid}`,
            );
          }
        },
      );
    }

    return requiredFields;
  };

  public static dataIsRequired = (
    params: any,
    data: ResourceItemData[],
  ): boolean => {
    if (!params.node) {
      return false;
    }
    const rowData = params.node.data;
    const requiredFields: string[] = rowData.requiredFields || [];
    return requiredFields.includes(params.column.getColId());
  };

  public recreateColumnDefs = (
    data: ResourceItemData[],
    setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
    resource: Resource,
    dateFormats: Record<string, string>,
  ) => {
    //     //setting coldefs again to force ag-grid to recognize new rows
    this.agGrid.api.setColumnDefs(
      GridBatchCreateResourceItemsService.getDefaultColDefs(
        resource,
        data,
        setData,
        dateFormats,
      ),
    );
  };
}
